import moment from "moment";

import { Calendar } from "@/Appointments/Components/Calendar/Calendar";
import { IAppointmentEditModel } from "@/Appointments/Models/UI/IAppointmentEditModel";
import { ICalendarDisplaySettingsModel } from "@/Appointments/Models/UI/ICalendarDisplaySettingsModel";
import { ICalendarModel } from "@/Appointments/Models/UI/ICalendarModel";
import { ICalendarPrintByDoctorModel } from "@/Appointments/Models/UI/ICalendarPrintByDoctorModel";
import { ICalendarPrintDialogModel } from "@/Appointments/Models/UI/ICalendarPrintDialogModel";
import { ICalendarRulesetModel } from "@/Appointments/Models/UI/ICalendarRulesetModel";
import { EmptyProps } from "@/Common/Components/BaseReactComponent";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { EditAppointmentDialog } from "./Appointment/AppointmentEditDialog";
import { NoticeBoard } from "./NoticeBoard/NoticeBoard";
import { ComboAppointmentWarningPopup } from "./ComboAppointmentWarningPopup";
import { DoubleBookingDialog } from "./DoubleBookingDialog";
import { PrintCalendarDialog } from "./PrintCalendarDialog";
import { PrintMDDialog } from "./PrintMDDialog";

class CalendarTabState {
    public calendarModel: ICalendarModel;
    public calendarRulesetModel: ICalendarRulesetModel;
    public calendarDisplaySettingsModel: ICalendarDisplaySettingsModel;
    public calendarPrintByDoctorModel: ICalendarPrintByDoctorModel;
    public calendarPrintDialogModel: ICalendarPrintDialogModel;
    public appointmentEditModel: IAppointmentEditModel;
    public patientCreationModel: IPatientCreationModel;
    public patientBannerModel: IPatientBannerModel;
    public patientPortalModel: Person;
    public isSaving: boolean;

    public isNoticeBoard: boolean;
}

class CalendarTab extends React.Component<EmptyProps, CalendarTabState> {
    private changeListenerMethodId: string;
    private ramqChangeListenerMethodId: string;

    constructor(props: EmptyProps) {
        super(props);

        calendarStore.PrepareModelsForViewMount();

        // Close AppointmentEdit Popup When coming back to the tab
        if (calendarStore.calendarModel && calendarStore.calendarModel.get("IsEditAppointmentDialogVisible")) {
            calendarStore.calendarModel = calendarStore.calendarModel.set("IsEditAppointmentDialogVisible", false);
        }

        this.state = this.getStoreState();
    }

    public render(): JSX.Element {
        var confirmDoubleBookingDialog: JSX.Element;
        if (this.state.calendarModel && this.state.calendarModel.get("IsDoubleBookingDialogVisible")) {
            confirmDoubleBookingDialog = <DoubleBookingDialog cancelClickOp={LocalOperations.Calendar.AppointmentEdit_DoubleBooking_Cancel} continueClickOp={LocalOperations.Calendar.AppointmentEdit_DoubleBooking_Continue} />;
        }

        var printMDDialog: JSX.Element;
        if (this.state.calendarModel && this.state.calendarModel.get("IsPrintMDDialogVisible")) {
            printMDDialog = <PrintMDDialog Model={this.state.calendarPrintByDoctorModel} />;
        }

        var printCalendarDialog: JSX.Element;
        if (this.state.calendarModel && this.state.calendarModel.get("IsPrintCalendarDialogVisible")) {
            printCalendarDialog = <PrintCalendarDialog Model={this.state.calendarPrintDialogModel} />;
        }

        var comboAppointmentDialog: JSX.Element;
        if (this.state.calendarModel && this.state.calendarModel.get("ShowComboAppointmentModificationPopup")) {
            comboAppointmentDialog = <ComboAppointmentWarningPopup CancelClickTargetOperation={LocalOperations.Calendar.AppointmentEdit_OnCancel} OkClickedTargetOperation={LocalOperations.Calendar.AppointmentEdit_GroupConfirmSave} />;
        }

        return (
            <div className="sub-container">
                <div className="left-nav-menu closed transition">
                    <div className="ws-scrollable hide-overflow" />
                </div>

                {this.renderContent()}

                {this.state.calendarModel
                    && this.state.calendarModel.get("IsEditAppointmentDialogVisible")
                    && (
                        <EditAppointmentDialog
                            IsSaving={this.state.isSaving}
                            AppointmentEditModel={this.state.appointmentEditModel}
                            PatientBannerModel={this.state.patientBannerModel}
                            PatientCreationModel={this.state.patientCreationModel}
                            PatientPortalModel={this.state.patientPortalModel}
                            AppointmentTimePickerInterval={this.state.calendarDisplaySettingsModel.get("TimeSlotLength")}
                        />
                    )
                }

                {confirmDoubleBookingDialog}

                {printMDDialog}

                {printCalendarDialog}

                {comboAppointmentDialog}
            </div>
        );
    }

    private renderContent(): JSX.Element {
        if (this.state.isNoticeBoard)
            return <NoticeBoard shouldReloadQuestionnaireBoard={!this.state.isSaving} />;

        return <Calendar CalendarModel={this.state.calendarModel} CalendarRulesetModel={this.state.calendarRulesetModel} CalendarDisplaySettingsModel={this.state.calendarDisplaySettingsModel} />;
    }

    public componentDidMount(): void {
        this.changeListenerMethodId = calendarStore.AddChangeListener(this.changeListener, this, "calendar-tab-listener");
        this.ramqChangeListenerMethodId = ramqStore.AddChangeListener(this.ramqChangeListener, this, "ramq-tab-listener");

        const lastLoad = this.state.calendarModel.get("LastLoad");
        const needToLoadCalendar = (
            !lastLoad
            || moment(new Date()).diff(lastLoad.toMoment(), "seconds") > 60
        );

        if (needToLoadCalendar) {
            calendarStore.calendarActions.LoadCalendarAppointmentCounts(
                calendarStore.calendarModel,
                calendarStore.calendarDisplaySettingsModel,
                () => calendarStore.calendarActions.LoadCalendar(calendarStore.calendarModel, calendarStore.calendarDisplaySettingsModel, SubServerOperations.Calendar.SearchAppointments_ModificationReload)
            );
        }
    }

    public shouldComponentUpdate(nextProps: any, nextState: CalendarTabState): boolean {
        return (
            this.state.calendarModel !== nextState.calendarModel
            || this.state.calendarDisplaySettingsModel !== nextState.calendarDisplaySettingsModel
            || this.state.appointmentEditModel !== nextState.appointmentEditModel
            || this.state.patientCreationModel !== nextState.patientCreationModel
            || this.state.calendarPrintDialogModel !== nextState.calendarPrintDialogModel
            || this.state.calendarPrintByDoctorModel !== nextState.calendarPrintByDoctorModel
            || this.state.patientBannerModel !== nextState.patientBannerModel
            || this.state.isSaving !== nextState.isSaving
            || this.state.calendarRulesetModel !== nextState.calendarRulesetModel
            || this.state.isNoticeBoard !== nextState.isNoticeBoard
            || this.state !== nextState
        );
    }

    public componentDidUpdate(nextProps: any, nextState: CalendarTabState): void {
        if (this.state.isNoticeBoard !== nextState.isNoticeBoard)
            sessionStore.dispatch(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts);
    }

    public componentWillUnmount(): void {
        calendarStore.RemoveChangeListener(this.changeListenerMethodId);
        ramqStore.RemoveChangeListener(this.ramqChangeListenerMethodId);
    }

    public changeListener(): void {
        this.setState(this.getStoreState());
    }

    public getStoreState(): CalendarTabState {
        const storeState = new CalendarTabState();
        storeState.calendarModel = calendarStore.calendarModel;
        storeState.calendarRulesetModel = calendarStore.calendarRulesetModel;
        storeState.calendarDisplaySettingsModel = calendarStore.calendarDisplaySettingsModel;
        storeState.appointmentEditModel = calendarStore.appointmentEditModel;
        storeState.patientCreationModel = calendarStore.patientCreationModel;
        storeState.calendarPrintByDoctorModel = calendarStore.calendarPrintByDoctorModel;
        storeState.calendarPrintDialogModel = calendarStore.calendarPrintDialogModel;
        storeState.patientBannerModel = calendarStore.patientBannerModel;
        storeState.patientPortalModel = calendarStore.patientPortalModel;
        storeState.isSaving = calendarStore.isSaving;
        storeState.isNoticeBoard = calendarStore.isNoticeBoard;

        return storeState;
    }

    public ramqChangeListener(): void {
        if (calendarStore.patientBannerModel) {
            var patientModel = calendarStore.patientBannerModel.get("Model");
            calendarStore.patientBannerModel = calendarStore.patientBannerModel.delete("Model");
            calendarStore.patientBannerModel = calendarStore.patientBannerModel.set("Model", patientModel);
            this.changeListener();
        }
    }
}

export { CalendarTab };
